<script>
import AppPagination from '@/components/common/AppPagination.vue'
import { getBranchList } from '@/api/branch'
import { getAdminInfo } from '@/utils/token'

/**
 * 지점 목록 페이지.
 *
 * - HQ 관리자: 전체 지점 목록 표시, 등록 버튼 노출
 * - 지점 관리자: 목록 없이 자신의 지점 상세 페이지로 즉시 리다이렉트
 * - 행 클릭 시 `/branches/:branchCode` 상세 페이지로 이동
 */
export default {
  name: 'BranchListPage',
  components: { AppPagination },
  data() {
    return {
      currentAdmin: getAdminInfo(),
      search: { keyword: '', branchType: '', branchStatus: '' },
      branchTypeOptions: [
        { title: '전체', value: '' },
        { title: '직영점', value: 'DIRECT' },
        { title: '대리점', value: 'AGENCY' },
        { title: '가맹점', value: 'FRANCHISE' },
      ],
      branchStatusOptions: [
        { title: '전체', value: '' },
        { title: '운영중', value: 'ACTIVE' },
        { title: '일시중지', value: 'INACTIVE' },
        { title: '폐점', value: 'CLOSED' },
      ],
      items: [],
      total: 0,
      page: 1,
      size: 10,
      listLoading: false,
    }
  },
  computed: {
    isHq() {
      return this.currentAdmin?.adminType === 'HQ'
    },
  },
  mounted() {
    // 지점 관리자는 자신의 지점 상세로 바로 이동
    if (!this.isHq) {
      const branchCode = this.currentAdmin?.branchCode
      if (branchCode) {
        this.$router.replace(`/branches/${branchCode}`)
        return
      }
    }
    this.fetchBranchList()
  },
  methods: {
    async fetchBranchList() {
      this.listLoading = true
      try {
        const res = await getBranchList({
          keyword: this.search.keyword || undefined,
          branchType: this.search.branchType || undefined,
          branchStatus: this.search.branchStatus || undefined,
          page: this.page,
          size: this.size,
        })
        if (res.data.code === 0) {
          this.items = res.data.data.items || []
          this.total = res.data.data.total || 0
        } else {
          this.items = []
          this.total = 0
        }
      } catch {
        this.items = []
        this.total = 0
      } finally {
        this.listLoading = false
      }
    },

    handleSearch() {
      this.page = 1
      this.fetchBranchList()
    },

    handleReset() {
      this.search = { keyword: '', branchType: '', branchStatus: '' }
      this.page = 1
      this.fetchBranchList()
    },

    onPageChange(newPage) {
      this.page = newPage
      this.fetchBranchList()
    },

    onSizeChange(newSize) {
      this.size = newSize
      this.page = 1
      this.fetchBranchList()
    },

    /** 행 클릭 → 상세 페이지 이동 */
    goDetail(item) {
      this.$router.push(`/branches/${item.branchCode}`)
    },

    /** 등록 버튼 → 등록 페이지 이동 */
    goCreate() {
      this.$router.push('/branches/create')
    },

    getBranchTypeLabel(type) {
      const map = { HQ: '본사', DIRECT: '직영점', AGENCY: '대리점', FRANCHISE: '가맹점' }
      return map[type] || type
    },

    getBranchTypeColor(type) {
      const map = { HQ: 'purple', DIRECT: 'primary', AGENCY: 'info', FRANCHISE: 'success' }
      return map[type] || 'default'
    },

    getBranchStatusLabel(status) {
      const map = { ACTIVE: '운영중', INACTIVE: '일시중지', CLOSED: '폐점' }
      return map[status] || status
    },

    getBranchStatusColor(status) {
      const map = { ACTIVE: 'success', INACTIVE: 'warning', CLOSED: 'error' }
      return map[status] || 'default'
    },
  },
}
</script>

<template>
  <div>
    <!-- 페이지 헤더 -->
    <div class="d-flex align-center justify-space-between mb-1">
      <div>
        <div class="text-h5 font-weight-bold">지점관리</div>
        <div class="text-body-2 text-grey mt-1">직영점 · 대리점 · 가맹점 정보 관리</div>
      </div>
      <v-spacer />
      <v-btn
        v-if="isHq"
        color="primary"
        prepend-icon="mdi-plus"
        rounded="lg"
        elevation="0"
        @click="goCreate"
      >
        지점 등록
      </v-btn>
    </div>

    <!-- 검색 영역 -->
    <v-card rounded="lg" elevation="1" class="mt-5 mb-5">
      <v-card-item class="py-3 bg-orange-lighten-5">
        <v-card-title class="text-subtitle-2 font-weight-bold">검색 조건</v-card-title>
      </v-card-item>
      <v-card-text class="pt-4">
        <v-row class="align-center">
          <!-- 키워드 검색 -->
          <v-col cols="12" md="4">
            <v-text-field
              v-model="search.keyword"
              label="지점 검색"
              placeholder="지점명, 지점코드"
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              @keyup.enter="handleSearch"
            />
          </v-col>
          <!-- 지점구분 -->
          <v-col cols="12" md="2">
            <v-select
              v-model="search.branchType"
              :items="branchTypeOptions"
              item-title="title"
              item-value="value"
              label="지점구분"
              variant="outlined"
              density="compact"
              hide-details
            />
          </v-col>
          <!-- 운영상태 -->
          <v-col cols="12" md="2">
            <v-select
              v-model="search.branchStatus"
              :items="branchStatusOptions"
              item-title="title"
              item-value="value"
              label="운영상태"
              variant="outlined"
              density="compact"
              hide-details
            />
          </v-col>
          <v-col cols="12" md="4" class="d-flex justify-end ga-2 search-actions">
            <v-btn color="primary" prepend-icon="mdi-magnify" @click="handleSearch">검색</v-btn>
            <v-btn variant="outlined" prepend-icon="mdi-refresh" @click="handleReset">초기화</v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- 지점 목록 -->
    <v-card rounded="lg" elevation="1">
      <v-card-item class="py-3">
        <v-card-title class="text-subtitle-1 font-weight-bold">
          지점 목록
          <v-chip size="small" color="primary" label class="ml-2">{{ total }}개</v-chip>
        </v-card-title>
      </v-card-item>

      <!-- 로딩 -->
      <v-progress-linear v-if="listLoading" indeterminate color="primary" />

      <v-table density="compact" class="border-t">
        <thead>
          <tr>
            <th class="text-center">번호</th>
            <th class="text-center">지점코드</th>
            <th class="text-center">지점명</th>
            <th class="text-center">구분</th>
            <th class="text-center">운영상태</th>
            <th class="text-center">주소</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, index) in items"
            :key="item.branchCode"
            style="cursor: pointer;"
            class="branch-row"
            @click="goDetail(item)"
          >
            <td class="text-center text-caption text-grey">
              {{ total - (page - 1) * size - index }}
            </td>
            <td class="text-center text-caption text-grey-darken-1">{{ item.branchCode }}</td>
            <td class="text-center font-weight-medium">{{ item.branchName || '-' }}</td>
            <td class="text-center">
              <v-chip
                :color="getBranchTypeColor(item.branchType)"
                size="x-small"
                label
                variant="tonal"
              >
                {{ getBranchTypeLabel(item.branchType) }}
              </v-chip>
            </td>
            <td class="text-center">
              <v-chip
                :color="getBranchStatusColor(item.branchStatus)"
                size="x-small"
                label
                variant="tonal"
              >
                {{ getBranchStatusLabel(item.branchStatus) }}
              </v-chip>
            </td>
            <td class="text-center text-body-2">{{ item.address1 || '-' }}</td>
          </tr>

          <!-- 데이터 없음 -->
          <tr v-if="!listLoading && items.length === 0">
            <td colspan="6" class="text-center text-grey py-10">
              <v-icon size="40" class="d-block mx-auto mb-2" color="grey-lighten-2">
                mdi-store-off-outline
              </v-icon>
              검색 결과가 없습니다.
            </td>
          </tr>
        </tbody>
      </v-table>

      <!-- 페이지네이션 -->
      <div class="px-4 pb-4">
        <AppPagination
          :total="total"
          :page="page"
          :size="size"
          @update:page="onPageChange"
          @update:size="onSizeChange"
        />
      </div>
    </v-card>
  </div>
</template>

<style scoped>
.branch-row:hover {
  background-color: rgba(205, 178, 122, 0.06);
}

@media (max-width: 959px) {
  .search-actions {
    justify-content: flex-start !important;
  }
}
</style>
